import React from 'react';
import { AgentFilledIcon } from '@domain-group/fe-co-icon';
import {
  AgencySearchProfileCardFragment,
  ContactSearchProfileCardFragment,
} from '../../../../generated/graphql';
import { formatCurrency, notEmpty, startCase } from '../../../../utils';
import {
  TapAgentCardProps,
  TapAgencyCardProps,
  SearchResultsBaseProperties,
} from '../../../../tracking/search-results';
import { useAgencyRating, useContactRating } from '../../hooks';
import { PAGE_SIZE } from '../../page-size';
import { SearchResultCard } from '../search-result-card';
import Avatar, { isNoAgentPhoto } from '../../../shared/avatar';
import { CardRating } from '../card-rating';
import { AvatarStack, hasAvatars } from '../avatar-stack';
import * as styles from './search-result-cards.style';

export interface SearchResultCardsProps {
  isAgency: boolean;
  isPropertyManager?: boolean;
  agentResults?: Array<ContactSearchProfileCardFragment | null> | null;
  agencyResults?: Array<AgencySearchProfileCardFragment | null> | null;
  suburbName?: string;
  page?: number;
  baseProperties?: SearchResultsBaseProperties;
  onTapAgentCard?: (props: TapAgentCardProps) => void;
  onTapAgencyCard?: (props: TapAgencyCardProps) => void;
}

const withTrailingSlash = (path: string): string =>
  path.endsWith('/') ? path : `${path}/`;

const jointSalesFootnote = (count?: number | null): string | undefined =>
  count
    ? `* Plus ${count} sale${count > 1 ? 's' : ''} as joint agent`
    : undefined;

const titleCase = (str: string): string =>
  str.split(' ').map(startCase).join(' ');

const Stat = ({
  value,
  label,
}: {
  value: React.ReactNode;
  label: string;
}): JSX.Element => (
  <div css={styles.stat}>
    <div css={styles.statValue}>{value}</div>
    <div css={styles.statLabel}>{label}</div>
  </div>
);

const AgentPhoto = ({
  contact,
}: {
  contact: ContactSearchProfileCardFragment;
}): JSX.Element =>
  contact.profilePhoto && !isNoAgentPhoto(contact.profilePhoto) ? (
    <Avatar
      css={styles.agentAvatar}
      imageUrl={contact.profilePhoto}
      imageTitle={contact.name}
      shape="square"
      size="custom"
    />
  ) : (
    <div css={styles.agentAvatarPlaceholder}>
      <AgentFilledIcon
        viewBox="0 0 18 20"
        css={styles.agentAvatarPlaceholderIcon}
      />
    </div>
  );

const ContactCard = ({
  contact,
  suburbName,
  isPropertyManager,
  priority,
  suggestionPosition,
  baseProperties,
  onTapAgentCard,
}: {
  contact: ContactSearchProfileCardFragment;
  suburbName?: string;
  isPropertyManager?: boolean;
  priority?: boolean;
  suggestionPosition: number;
  baseProperties?: SearchResultsBaseProperties;
  onTapAgentCard?: (props: TapAgentCardProps) => void;
}): JSX.Element => {
  const { rating, depthTier, reviewCount, starRating } =
    useContactRating(contact);

  const ratingBlock = (inline: boolean) =>
    rating ? <CardRating {...rating} inline={inline} /> : undefined;

  const tapAgentCard = onTapAgentCard
    ? () =>
        onTapAgentCard({
          baseProperties,
          depthTier,
          suggestionPosition,
          agentRating: Boolean(rating),
          reviewCount,
          starRating,
        })
    : undefined;

  return (
    <SearchResultCard
      onViewProfile={tapAgentCard}
      profileUrl={withTrailingSlash(`/real-estate-agent/${contact.profileUrl}`)}
      name={contact.name}
      subtitle={
        contact.jobTitle ||
        (contact.jobCategory ? titleCase(contact.jobCategory) : undefined)
      }
      agencyName={contact.agencyName}
      officeLine={contact.agencyName}
      officeLineAsHeading
      hideOfficeLineOnMobile
      logoUrl={contact.agencyLogoUrl ?? undefined}
      logoColour={contact.brandColour ?? undefined}
      priority={priority}
      media={<AgentPhoto contact={contact} />}
      rating={ratingBlock(false)}
      mobileRating={ratingBlock(true)}
      metricsLabel={suburbName ? `Past 12 months in ${suburbName}` : undefined}
      metrics={
        isPropertyManager ? (
          <>
            <Stat value={contact.totalForRent || '—'} label="For rent" />
            <Stat value={contact.totalJointLeased || '—'} label="Leased" />
          </>
        ) : (
          <>
            <Stat
              value={contact.totalSoldAndAuctioned || '—'}
              label="Total sales"
            />
            <Stat
              value={
                contact.averageSoldDaysOnMarket
                  ? Math.floor(contact.averageSoldDaysOnMarket)
                  : '—'
              }
              label="Avg days listed"
            />
            <Stat
              value={
                contact.averageSoldPrice
                  ? formatCurrency(contact.averageSoldPrice)
                  : '—'
              }
              label="Avg sold price"
            />
          </>
        )
      }
      footnote={
        isPropertyManager
          ? undefined
          : jointSalesFootnote(contact.totalJointSoldAndAuctioned)
      }
    />
  );
};

const agencyAvatars = (agency: AgencySearchProfileCardFragment) =>
  hasAvatars(agency.agents) ? (
    <AvatarStack agents={agency.agents} />
  ) : undefined;

const AgencyCard = ({
  agency,
  suburbName,
  priority,
  suggestionPosition,
  baseProperties,
  onTapAgencyCard,
}: {
  agency: AgencySearchProfileCardFragment;
  suburbName?: string;
  priority?: boolean;
  suggestionPosition: number;
  baseProperties?: SearchResultsBaseProperties;
  onTapAgencyCard?: (props: TapAgencyCardProps) => void;
}): JSX.Element => {
  const { rating, subCategory2, reviewRating, reviewCount, starRating } =
    useAgencyRating(agency);

  const ratingBlock = (inline: boolean) =>
    rating ? <CardRating {...rating} inline={inline} /> : undefined;

  const tapAgencyCard = onTapAgencyCard
    ? () =>
        onTapAgencyCard({
          baseProperties,
          subCategory2,
          cardPosition: suggestionPosition,
          reviewRating,
          reviewCount,
          starRating,
          agencyId: agency.id,
          agencyName: agency.name,
        })
    : undefined;

  return (
    <SearchResultCard
      onViewProfile={tapAgencyCard}
      profileUrl={`/real-estate-agencies/${agency.profileUrl}`}
      name={agency.name}
      agencyName={agency.name}
      officeLine={
        agency.hideOfficeLocation
          ? undefined
          : (agency.displayAddress ?? undefined)
      }
      logoUrl={agency.logoSmall ?? undefined}
      logoColour={agency.brandColour ?? undefined}
      priority={priority}
      media={agencyAvatars(agency)}
      stackMediaOnMobile
      rating={ratingBlock(false)}
      mobileRating={ratingBlock(true)}
      metricsLabel={suburbName ? `Past 12 months in ${suburbName}` : undefined}
      statsColumns={5}
      metrics={
        <>
          <Stat value={agency.totalSoldAndAuctioned ?? '—'} label="Sold" />
          <Stat value={agency.totalForSale ?? '—'} label="For sale" />
          <Stat value={agency.totalLeased ?? '—'} label="Leased" />
          <Stat value={agency.totalForRent ?? '—'} label="For rent" />
        </>
      }
    />
  );
};

const SearchResultCards = ({
  isAgency,
  isPropertyManager,
  agentResults,
  agencyResults,
  suburbName,
  page = 1,
  baseProperties,
  onTapAgentCard,
  onTapAgencyCard,
}: SearchResultCardsProps): JSX.Element => (
  <>
    {isAgency
      ? agencyResults
          ?.filter(notEmpty)
          .map((agency, index) => (
            <AgencyCard
              key={agency.id}
              agency={agency}
              suburbName={suburbName}
              priority={index === 0}
              suggestionPosition={(page - 1) * PAGE_SIZE + index + 1}
              baseProperties={baseProperties}
              onTapAgencyCard={onTapAgencyCard}
            />
          ))
      : agentResults
          ?.filter(notEmpty)
          .map((contact, index) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              suburbName={suburbName}
              isPropertyManager={isPropertyManager}
              priority={index === 0}
              suggestionPosition={(page - 1) * PAGE_SIZE + index + 1}
              baseProperties={baseProperties}
              onTapAgentCard={onTapAgentCard}
            />
          ))}
  </>
);

export default SearchResultCards;
